import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { STAFF_CUSTOMER_DEVICES } from '@scio3/graphql-client';
import type { StaffDevice } from '@scio3/shared-types';
import { useMdmDevicesFilterStore } from '../stores/mdm-devices-filter-store';
import { useMdmDevicesSortStore } from '../stores/mdm-devices-sort-store';

/**
 * Staff cross-tenant variant of `useMdmDevices` — reads via the staff-gated
 * `staffCustomerDevices` GraphQL query. Same return shape as the customer
 * hook so the COPIED UI components in `apps/staff/features/mdm-devices/`
 * render unchanged.
 *
 * See: scio3/docs/specs/19-optiedge-mdm/staff-devices-sync.md
 *   "the copied UI is kept structurally identical; only the query hook differs."
 *
 * Filter + sort are applied CLIENT-SIDE (the backend query currently returns
 * ≤ 100 devices per accountId — cheap to sort/filter in memory). Real-time
 * subscriptions are intentionally skipped on staff side: pubsub is team-scoped
 * and staff are cross-tenant viewers, not writers.
 */

/** Local device type mirrors `MdmDevice` from apps/web so copied components typecheck. */
export type MdmDevice = StaffDevice & { fleetId?: string | null };

interface StaffCustomerDevicesResult {
  staffCustomerDevices: {
    items: MdmDevice[];
    total: number;
  };
}

interface UseStaffCustomerDevicesOptions {
  accountId: string | undefined;
  fleetIdOverride?: string | null;
}

export function useStaffCustomerDevices(options: UseStaffCustomerDevicesOptions) {
  const { accountId, fleetIdOverride } = options;

  const searchText = useMdmDevicesFilterStore((s) => s.searchText);
  const fleetIdFromStore = useMdmDevicesFilterStore((s) => s.fleetId);
  const tags = useMdmDevicesFilterStore((s) => s.tags);

  const sortField = useMdmDevicesSortStore((s) => s.sortField);
  const sortDirection = useMdmDevicesSortStore((s) => s.sortDirection);

  const {
    data,
    loading,
    error,
    refetch: rawRefetch,
  } = useQuery<StaffCustomerDevicesResult>(STAFF_CUSTOMER_DEVICES, {
    variables: { accountId: accountId ?? '' },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });

  const allItems = useMemo(() => data?.staffCustomerDevices?.items ?? [], [data]);
  const total = data?.staffCustomerDevices?.total ?? allItems.length;

  // Resolve effective fleet filter: prop override > store value > "all".
  const effectiveFleetFilter = fleetIdOverride !== undefined ? fleetIdOverride : fleetIdFromStore;

  const devices = useMemo<MdmDevice[]>(() => {
    let list = allItems.slice();

    if (searchText.trim()) {
      const needle = searchText.trim().toLowerCase();
      list = list.filter((d) => (d.deviceName ?? '').toLowerCase().includes(needle));
    }
    if (tags.length > 0) {
      list = list.filter((d) =>
        Array.isArray(d.tags) ? d.tags.some((tag) => tags.includes(tag)) : false,
      );
    }
    if (effectiveFleetFilter !== undefined) {
      if (effectiveFleetFilter === null) {
        list = list.filter((d) => !d.fleetId);
      } else {
        list = list.filter((d) => d.fleetId === effectiveFleetFilter);
      }
    }

    // Sort in memory — match the backend's accepted fields.
    const direction = sortDirection === 'ASC' ? 1 : -1;
    list.sort((a, b) => {
      const av = pickSortable(a, sortField);
      const bv = pickSortable(b, sortField);
      if (av === bv) return 0;
      return av > bv ? direction : -direction;
    });

    return list;
  }, [allItems, searchText, tags, effectiveFleetFilter, sortField, sortDirection]);

  const refetch = useCallback(() => {
    if (!accountId) return;
    rawRefetch({ accountId });
  }, [rawRefetch, accountId]);

  return {
    devices,
    loading,
    error,
    totalCount: total,
    hasNextPage: false,
    loadMore: () => undefined,
    isLoadingMore: false,
    refetch,
  };
}

function pickSortable(d: MdmDevice, field: 'deviceName' | 'creationDate' | 'currentSelectionDate') {
  if (field === 'deviceName') return (d.deviceName ?? '').toLowerCase();
  if (field === 'creationDate') return (d.activationDate as unknown as string) ?? '';
  // currentSelectionDate is not exposed on StaffDevice — fall back to activation.
  return (d.activationDate as unknown as string) ?? '';
}
